import { useState, useEffect, useMemo, useCallback } from "react"
import toast from "react-hot-toast"
import { lumi } from "../lib/lumi"
import { useAuth } from "../hooks/useAuth"
import {
  useTeamSchedules,
  SCHEDULE_TYPE_LIST,
  SCHEDULE_TYPE_META,
  type ScheduleType,
  type TeamSchedule,
  type ScheduleInput,
  type ScheduleAttendee,
  canEditSchedule,
  expandDateRange,
  formatAttendees,
  getMonthRange,
  getScheduleTitle,
  toDateKey,
} from "../hooks/useTeamSchedules"
import { ScheduleEventModal } from "./ScheduleEventModal"
import {
  ScheduleDayDetailModal,
  ScheduleDetailModal,
} from "./ScheduleDayDetailModal"
import { getHolidayName, isHoliday } from "../lib/koreanHolidays"
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Search,
  Loader2,
  AlertCircle,
  CalendarDays,
  X,
} from "lucide-react"

const WEEKDAY_LABELS = ["일", "월", "화", "수", "목", "금", "토"]
const MAX_PER_CELL = 4

interface CalendarCell {
  dateKey: string
  day: number
  isCurrentMonth: boolean
  dayOfWeek: number
}

export function TeamCalendar() {
  const auth = useAuth()
  const {
    schedules,
    loading,
    error,
    fetchMonth,
    createSchedule,
    updateSchedule,
    deleteSchedule,
  } = useTeamSchedules()

  const today = new Date()
  const [viewYear, setViewYear] = useState(today.getFullYear())
  const [viewMonth, setViewMonth] = useState(today.getMonth() + 1)

  const [teamFilter, setTeamFilter] = useState("all")
  const [userFilter, setUserFilter] = useState("all")
  const [userSearch, setUserSearch] = useState("")
  const [typeFilter, setTypeFilter] = useState<Set<ScheduleType>>(new Set())
  const [onlyMine, setOnlyMine] = useState(false)

  const [eventModalOpen, setEventModalOpen] = useState(false)
  const [editingSchedule, setEditingSchedule] = useState<TeamSchedule | null>(null)
  const [defaultDate, setDefaultDate] = useState<string>("")
  const [detailSchedule, setDetailSchedule] = useState<TeamSchedule | null>(null)
  const [dayDetailKey, setDayDetailKey] = useState<string | null>(null)

  const myUserId = auth?.user?.userId || ""
  const myUserName = auth?.userName || ""
  const myEmail = auth?.user?.email || ""
  const isAdmin = Boolean(auth?.isAdmin)

  // 월 데이터 조회
  useEffect(() => {
    fetchMonth(viewYear, viewMonth)
  }, [viewYear, viewMonth, fetchMonth])

  // 필터 후보 (일정에 등장한 팀·사용자 기준)
  const teamOptions = useMemo(() => {
    const set = new Set<string>()
    schedules.forEach((s) =>
      s.attendees.forEach((a) => {
        if (a.teamName) set.add(a.teamName)
      }),
    )
    return Array.from(set).sort()
  }, [schedules])

  const userOptions = useMemo(() => {
    const map = new Map<string, { userId: string; userName: string; teamName: string }>()
    schedules.forEach((s) =>
      s.attendees.forEach((a) => {
        if (!map.has(a.userId)) {
          map.set(a.userId, {
            userId: a.userId,
            userName: a.userName,
            teamName: a.teamName ?? "",
          })
        }
      }),
    )
    return Array.from(map.values()).sort((a, b) =>
      a.userName.localeCompare(b.userName, "ko"),
    )
  }, [schedules])

  const filteredUserOptions = useMemo(() => {
    const keyword = userSearch.trim().toLowerCase()
    if (!keyword) return userOptions
    return userOptions.filter((u) =>
      `${u.userName} ${u.teamName}`.toLowerCase().includes(keyword),
    )
  }, [userOptions, userSearch])

  // 필터 적용
  const filteredSchedules = useMemo(() => {
    return schedules.filter((s) => {
      if (typeFilter.size > 0 && !typeFilter.has(s.scheduleType)) return false
      if (teamFilter !== "all") {
        const hasTeam = s.attendees.some((a) => a.teamName === teamFilter)
        if (!hasTeam) return false
      }
      if (userFilter !== "all") {
        const hasUser = s.attendees.some((a) => a.userId === userFilter)
        if (!hasUser) return false
      }
      if (onlyMine) {
        const mine = s.attendees.some((a) => a.userId === myUserId)
        if (!mine) return false
      }
      return true
    })
  }, [schedules, typeFilter, teamFilter, userFilter, onlyMine, myUserId])

  // 날짜별 전개
  const schedulesByDate = useMemo(() => {
    const map = new Map<string, TeamSchedule[]>()
    filteredSchedules.forEach((s) => {
      expandDateRange(s.startDate, s.endDate).forEach((dateKey) => {
        const arr = map.get(dateKey) ?? []
        arr.push(s)
        map.set(dateKey, arr)
      })
    })
    return map
  }, [filteredSchedules])

  // 이번 달 일정 건수
  const monthCount = useMemo(() => {
    const { start, end } = getMonthRange(viewYear, viewMonth)
    return filteredSchedules.filter(
      (s) => s.startDate <= end && s.endDate >= start,
    ).length
  }, [filteredSchedules, viewYear, viewMonth])

  // 달력 그리드 (일요일 시작, 최대 6주)
  const calendarCells = useMemo<CalendarCell[]>(() => {
    const firstOfMonth = new Date(viewYear, viewMonth - 1, 1)
    const startDayOfWeek = firstOfMonth.getDay()
    const gridStart = new Date(firstOfMonth)
    gridStart.setDate(gridStart.getDate() - startDayOfWeek)

    const lastOfMonth = new Date(viewYear, viewMonth, 0)
    const totalDaysNeeded = startDayOfWeek + lastOfMonth.getDate()
    const totalCells = Math.ceil(totalDaysNeeded / 7) * 7

    const cells: CalendarCell[] = []
    for (let i = 0; i < totalCells; i += 1) {
      const d = new Date(gridStart)
      d.setDate(d.getDate() + i)
      cells.push({
        dateKey: toDateKey(d),
        day: d.getDate(),
        isCurrentMonth: d.getMonth() + 1 === viewMonth && d.getFullYear() === viewYear,
        dayOfWeek: d.getDay(),
      })
    }
    return cells
  }, [viewYear, viewMonth])

  const todayKey = toDateKey(today)

  // 월 이동
  const moveMonth = (delta: number) => {
    const next = new Date(viewYear, viewMonth - 1 + delta, 1)
    setViewYear(next.getFullYear())
    setViewMonth(next.getMonth() + 1)
  }

  const goToday = () => {
    const now = new Date()
    setViewYear(now.getFullYear())
    setViewMonth(now.getMonth() + 1)
  }

  const toggleType = (type: ScheduleType) => {
    setTypeFilter((prev) => {
      const next = new Set(prev)
      if (next.has(type)) next.delete(type)
      else next.add(type)
      return next
    })
  }

  const resetFilters = () => {
    setTeamFilter("all")
    setUserFilter("all")
    setUserSearch("")
    setTypeFilter(new Set())
    setOnlyMine(false)
  }

  const hasActiveFilter =
    teamFilter !== "all" ||
    userFilter !== "all" ||
    typeFilter.size > 0 ||
    onlyMine

  // 등록 모달 열기
  const openCreate = useCallback(
    (dateKey?: string) => {
      setEditingSchedule(null)
      setDefaultDate(dateKey ?? todayKey)
      setEventModalOpen(true)
    },
    [todayKey],
  )

  const openEdit = (schedule: TeamSchedule) => {
    setEditingSchedule(schedule)
    setDetailSchedule(null)
    setDayDetailKey(null)
    setEventModalOpen(true)
  }

  const myAttendee = useMemo<ScheduleAttendee[]>(() => {
    if (!myUserId) return []
    return [
      {
        userId: myUserId,
        userName: myUserName || "나",
        teamName: auth?.teamName || "",
        email: myEmail,
      },
    ]
  }, [myUserId, myUserName, myEmail, auth?.teamName])

  // 저장
  const handleSubmit = async (input: ScheduleInput) => {
    if (editingSchedule) {
      await updateSchedule(editingSchedule._id, input)
      toast.success("일정이 수정되었습니다.")
    } else {
      await createSchedule({
        ...input,
        creatorName: myUserName,
        creatorEmail: myEmail,
      })
      toast.success("일정이 등록되었습니다.")
    }
    setEventModalOpen(false)
    setEditingSchedule(null)
    await fetchMonth(viewYear, viewMonth)
  }

  // 삭제
  const handleDelete = async () => {
    if (!editingSchedule) return
    await deleteSchedule(editingSchedule._id)
    toast.success("일정이 삭제되었습니다.")
    setEventModalOpen(false)
    setEditingSchedule(null)
    await fetchMonth(viewYear, viewMonth)
  }

  // 모바일 아젠다 (이번 달, 날짜순)
  const agendaList = useMemo(() => {
    const { start, end } = getMonthRange(viewYear, viewMonth)
    const items: { dateKey: string; schedule: TeamSchedule }[] = []
    filteredSchedules.forEach((s) => {
      expandDateRange(s.startDate, s.endDate).forEach((dateKey) => {
        if (dateKey >= start && dateKey <= end) {
          items.push({ dateKey, schedule: s })
        }
      })
    })
    return items.sort((a, b) => {
      if (a.dateKey !== b.dateKey) return a.dateKey.localeCompare(b.dateKey)
      return a.schedule.startDate.localeCompare(b.schedule.startDate)
    })
  }, [filteredSchedules, viewYear, viewMonth])

  const dayDetailSchedules = useMemo(() => {
    if (!dayDetailKey) return []
    return schedulesByDate.get(dayDetailKey) ?? []
  }, [dayDetailKey, schedulesByDate])

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <div className="mx-auto max-w-7xl px-3 py-4 sm:px-6 sm:py-6">
        {/* 헤더 */}
        <div className="mb-4 flex flex-col gap-3 sm:mb-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">일정표</h1>
            <p className="mt-0.5 text-xs text-gray-500 sm:text-sm">
              전사 공유 캘린더 · 이번 달 일정{" "}
              <span className="font-semibold text-[#F59E0B]">{monthCount}건</span>
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={goToday}
              className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-700 transition hover:bg-gray-50 sm:text-sm"
            >
              오늘
            </button>
            <button
              type="button"
              onClick={() => openCreate()}
              className="inline-flex items-center gap-1.5 rounded-lg bg-[#F59E0B] px-3 py-2 text-xs font-semibold text-white transition hover:bg-[#D97706] sm:text-sm"
            >
              <Plus className="h-4 w-4" />
              일정 등록
            </button>
          </div>
        </div>

        {/* 필터 */}
        <div className="mb-4 rounded-xl border border-gray-200 bg-white p-3 sm:p-4">
          <div className="flex flex-col gap-2.5 sm:flex-row sm:flex-wrap sm:items-center">
            {/* 팀 */}
            <select
              value={teamFilter}
              onChange={(e) => setTeamFilter(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-xs focus:border-[#F59E0B] focus:outline-none focus:ring-2 focus:ring-[#F59E0B]/20 sm:w-auto sm:text-sm"
            >
              <option value="all">전체 팀</option>
              {teamOptions.map((team) => (
                <option key={team} value={team}>
                  {team}
                </option>
              ))}
            </select>

            {/* 사용자 검색형 드롭다운 */}
            <div className="relative w-full sm:w-56">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
              <select
                value={userFilter}
                onChange={(e) => setUserFilter(e.target.value)}
                className="w-full rounded-lg border border-gray-300 py-2 pl-8 pr-3 text-xs focus:border-[#F59E0B] focus:outline-none focus:ring-2 focus:ring-[#F59E0B]/20 sm:text-sm"
              >
                <option value="all">전체 사용자</option>
                {filteredUserOptions.map((u) => (
                  <option key={u.userId} value={u.userId}>
                    {u.userName}
                    {u.teamName ? ` (${u.teamName})` : ""}
                  </option>
                ))}
              </select>
            </div>

            {/* 내 일정만 */}
            <button
              type="button"
              onClick={() => setOnlyMine((v) => !v)}
              className={`rounded-lg border px-3 py-2 text-xs font-semibold transition sm:text-sm ${
                onlyMine
                  ? "border-[#F59E0B] bg-orange-50 text-[#B45309]"
                  : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
              }`}
            >
              내 일정만 보기
            </button>

            {hasActiveFilter && (
              <button
                type="button"
                onClick={resetFilters}
                className="inline-flex items-center gap-1 rounded-lg px-2 py-2 text-xs font-medium text-gray-500 transition hover:text-gray-700"
              >
                <X className="h-3.5 w-3.5" />
                필터 초기화
              </button>
            )}
          </div>

          {/* 유형 칩 */}
          <div className="mt-3 flex flex-wrap gap-1.5 border-t border-gray-100 pt-3">
            {SCHEDULE_TYPE_LIST.map((meta) => {
              const active = typeFilter.has(meta.key)
              return (
                <button
                  key={meta.key}
                  type="button"
                  onClick={() => toggleType(meta.key)}
                  className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold transition sm:text-xs ${
                    typeFilter.size === 0
                      ? meta.chip
                      : active
                        ? `${meta.chip} ring-2 ring-[#F59E0B] ring-offset-1`
                        : "border-gray-200 bg-white text-gray-400"
                  }`}
                >
                  {meta.label}
                </button>
              )
            })}
          </div>
        </div>

        {/* 월 이동 */}
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-1 sm:gap-2">
            <button
              type="button"
              onClick={() => moveMonth(-1)}
              className="rounded-lg border border-gray-300 bg-white p-1.5 text-gray-600 transition hover:bg-gray-50"
              aria-label="이전 달"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <h2 className="min-w-[120px] text-center text-base font-bold text-gray-900 sm:min-w-[150px] sm:text-lg">
              {viewYear}년 {viewMonth}월
            </h2>
            <button
              type="button"
              onClick={() => moveMonth(1)}
              className="rounded-lg border border-gray-300 bg-white p-1.5 text-gray-600 transition hover:bg-gray-50"
              aria-label="다음 달"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          {loading && (
            <span className="flex items-center gap-1.5 text-xs text-gray-500">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              불러오는 중
            </span>
          )}
        </div>

        {/* 오류 */}
        {error && !loading ? (
          <div className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3">
            <p className="flex items-center gap-2 text-sm text-rose-700">
              <AlertCircle className="h-4 w-4 shrink-0" />
              {error}
            </p>
            <button
              type="button"
              onClick={() => fetchMonth(viewYear, viewMonth)}
              className="shrink-0 rounded-lg border border-rose-300 bg-white px-3 py-1.5 text-xs font-semibold text-rose-700 transition hover:bg-rose-50"
            >
              다시 시도
            </button>
          </div>
        ) : null}

        {/* ==================== 데스크톱 달력 ==================== */}
        <div className="hidden overflow-hidden rounded-xl border border-gray-200 bg-white sm:block">
          <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50">
            {WEEKDAY_LABELS.map((label, idx) => (
              <div
                key={label}
                className={`py-2 text-center text-xs font-semibold ${
                  idx === 0
                    ? "text-rose-500"
                    : idx === 6
                      ? "text-blue-500"
                      : "text-gray-600"
                }`}
              >
                {label}
              </div>
            ))}
          </div>

          {loading ? (
            <div className="grid grid-cols-7">
              {Array.from({ length: 35 }).map((_, i) => (
                <div
                  key={i}
                  className="h-24 border-b border-r border-gray-100 bg-gray-50/50 p-1.5"
                >
                  <div className="h-3 w-6 animate-pulse rounded bg-gray-200" />
                  <div className="mt-2 space-y-1">
                    <div className="h-3 w-full animate-pulse rounded bg-gray-100" />
                    <div className="h-3 w-4/5 animate-pulse rounded bg-gray-100" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-7">
              {calendarCells.map((cell) => {
                const daySchedules = schedulesByDate.get(cell.dateKey) ?? []
                const visible = daySchedules.slice(0, MAX_PER_CELL)
                const overflow = daySchedules.length - visible.length
                const holidayName = isHoliday(cell.dateKey)
                  ? getHolidayName(cell.dateKey)
                  : ""
                const isToday = cell.dateKey === todayKey
                const isSunday = cell.dayOfWeek === 0
                const isSaturday = cell.dayOfWeek === 6

                return (
                  <div
                    key={cell.dateKey}
                    className={`min-h-[104px] border-b border-r border-gray-100 p-1.5 transition ${
                      cell.isCurrentMonth ? "bg-white" : "bg-gray-50/60"
                    } ${isToday ? "ring-2 ring-inset ring-[#F59E0B]" : ""}`}
                    onClick={() => openCreate(cell.dateKey)}
                  >
                    <div className="mb-1 flex items-start justify-between">
                      <span
                        className={`inline-flex h-5 min-w-5 items-center justify-center rounded px-1 text-[11px] font-bold ${
                          isToday
                            ? "bg-[#F59E0B] text-white"
                            : holidayName
                              ? "text-rose-600"
                              : isSunday
                                ? "text-rose-500"
                                : isSaturday
                                  ? "text-blue-500"
                                  : cell.isCurrentMonth
                                    ? "text-gray-700"
                                    : "text-gray-300"
                        }`}
                      >
                        {cell.day}
                      </span>
                    </div>

                    {holidayName && (
                      <p className="mb-1 truncate text-[9px] font-medium leading-tight text-rose-500">
                        {holidayName}
                      </p>
                    )}

                    <div className="space-y-0.5">
                      {visible.map((schedule) => {
                        const meta = SCHEDULE_TYPE_META[schedule.scheduleType]
                        return (
                          <button
                            key={`${cell.dateKey}-${schedule._id}`}
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation()
                              setDetailSchedule(schedule)
                            }}
                            className={`block w-full truncate rounded border px-1 py-0.5 text-left text-[10px] font-medium leading-tight transition hover:brightness-95 ${meta?.chip ?? "bg-gray-100 text-gray-700 border-gray-200"}`}
                            title={`[${meta?.label ?? ""}] ${getScheduleTitle(schedule)} · ${formatAttendees(schedule.attendees)}`}
                          >
                            <span className="font-bold">{meta?.label ?? ""}</span>{" "}
                            {formatAttendees(schedule.attendees)}
                          </button>
                        )
                      })}
                      {overflow > 0 && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation()
                            setDayDetailKey(cell.dateKey)
                          }}
                          className="block w-full rounded px-1 py-0.5 text-left text-[10px] font-semibold text-gray-500 transition hover:bg-gray-100"
                        >
                          +{overflow}건 더보기
                        </button>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* ==================== 모바일 달력 (점 표시) ==================== */}
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white sm:hidden">
          <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50">
            {WEEKDAY_LABELS.map((label, idx) => (
              <div
                key={label}
                className={`py-1.5 text-center text-[10px] font-semibold ${
                  idx === 0
                    ? "text-rose-500"
                    : idx === 6
                      ? "text-blue-500"
                      : "text-gray-600"
                }`}
              >
                {label}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {calendarCells.map((cell) => {
              const daySchedules = schedulesByDate.get(cell.dateKey) ?? []
              const isToday = cell.dateKey === todayKey
              const isSunday = cell.dayOfWeek === 0
              const isSaturday = cell.dayOfWeek === 6
              const holiday = isHoliday(cell.dateKey)
              const uniqueTypes = Array.from(
                new Set(daySchedules.map((s) => s.scheduleType)),
              ).slice(0, 3)

              return (
                <button
                  key={cell.dateKey}
                  type="button"
                  onClick={() => {
                    if (daySchedules.length > 0) setDayDetailKey(cell.dateKey)
                    else openCreate(cell.dateKey)
                  }}
                  className={`flex min-h-[52px] flex-col items-center border-b border-r border-gray-100 py-1 transition ${
                    cell.isCurrentMonth ? "bg-white" : "bg-gray-50/60"
                  } ${isToday ? "ring-2 ring-inset ring-[#F59E0B]" : ""}`}
                >
                  <span
                    className={`inline-flex h-5 min-w-5 items-center justify-center rounded text-[11px] font-bold ${
                      isToday
                        ? "bg-[#F59E0B] text-white"
                        : holiday
                          ? "text-rose-600"
                          : isSunday
                            ? "text-rose-500"
                            : isSaturday
                              ? "text-blue-500"
                              : cell.isCurrentMonth
                                ? "text-gray-700"
                                : "text-gray-300"
                    }`}
                  >
                    {cell.day}
                  </span>
                  <div className="mt-1 flex gap-0.5">
                    {uniqueTypes.map((type) => (
                      <span
                        key={type}
                        className={`h-1.5 w-1.5 rounded-full ${SCHEDULE_TYPE_META[type]?.dot ?? "bg-gray-400"}`}
                      />
                    ))}
                  </div>
                </button>
              )
            })}
          </div>
        </div>

        {/* ==================== 모바일 아젠다 ==================== */}
        <div className="mt-4 sm:hidden">
          <h3 className="mb-2 flex items-center gap-1.5 text-sm font-bold text-gray-900">
            <CalendarDays className="h-4 w-4 text-[#F59E0B]" />
            이번 달 일정 목록
            <span className="text-xs font-normal text-gray-500">
              ({agendaList.length}건)
            </span>
          </h3>
          {loading ? (
            <div className="space-y-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <div
                  key={i}
                  className="h-16 animate-pulse rounded-lg border border-gray-200 bg-gray-50"
                />
              ))}
            </div>
          ) : agendaList.length === 0 ? (
            <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed border-gray-300 bg-white py-8">
              <CalendarDays className="h-10 w-10 text-gray-200" />
              <p className="text-sm text-gray-500">등록된 일정이 없습니다.</p>
              <button
                type="button"
                onClick={() => openCreate()}
                className="inline-flex items-center gap-1.5 rounded-lg bg-[#F59E0B] px-3 py-2 text-xs font-semibold text-white"
              >
                <Plus className="h-3.5 w-3.5" />
                일정 등록
              </button>
            </div>
          ) : (
            <ul className="space-y-2">
              {agendaList.map(({ dateKey, schedule }) => {
                const meta = SCHEDULE_TYPE_META[schedule.scheduleType]
                const d = new Date(`${dateKey}T00:00:00`)
                return (
                  <li key={`${dateKey}-${schedule._id}`}>
                    <button
                      type="button"
                      onClick={() => setDetailSchedule(schedule)}
                      className="flex w-full items-center gap-3 rounded-xl border border-gray-200 bg-white p-2.5 text-left transition active:bg-gray-50"
                    >
                      <div className="flex w-11 shrink-0 flex-col items-center">
                        <span
                          className={`text-[10px] font-semibold ${
                            d.getDay() === 0
                              ? "text-rose-500"
                              : d.getDay() === 6
                                ? "text-blue-500"
                                : "text-gray-500"
                          }`}
                        >
                          {WEEKDAY_LABELS[d.getDay()]}
                        </span>
                        <span className="text-base font-bold text-gray-900">
                          {d.getDate()}
                        </span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="mb-0.5 flex items-center gap-1.5">
                          <span
                            className={`shrink-0 rounded border px-1.5 py-0.5 text-[10px] font-semibold ${meta?.chip ?? ""}`}
                          >
                            {meta?.label ?? ""}
                          </span>
                          <span className="truncate text-xs font-semibold text-gray-900">
                            {getScheduleTitle(schedule)}
                          </span>
                        </div>
                        <p className="truncate text-[11px] text-gray-500">
                          {formatAttendees(schedule.attendees)}
                        </p>
                      </div>
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </div>

        {/* 데스크톱 빈 상태 */}
        {!loading && !error && filteredSchedules.length === 0 && (
          <div className="mt-4 hidden flex-col items-center gap-3 rounded-xl border border-dashed border-gray-300 bg-white py-10 sm:flex">
            <CalendarDays className="h-12 w-12 text-gray-200" />
            <p className="text-sm text-gray-500">
              등록된 일정이 없습니다.
              {hasActiveFilter ? " (필터를 초기화해보세요)" : ""}
            </p>
            <button
              type="button"
              onClick={() => openCreate()}
              className="inline-flex items-center gap-1.5 rounded-lg bg-[#F59E0B] px-3 py-2 text-sm font-semibold text-white transition hover:bg-[#D97706]"
            >
              <Plus className="h-4 w-4" />
              일정 등록
            </button>
          </div>
        )}
      </div>

      {/* 모달 */}
      {eventModalOpen && (
        <ScheduleEventModal
          schedule={editingSchedule}
          defaultDate={defaultDate}
          defaultAttendees={editingSchedule ? [] : myAttendee}
          currentUserId={myUserId}
          currentUserName={myUserName}
          currentUserEmail={myEmail}
          canEdit={
            editingSchedule
              ? canEditSchedule(editingSchedule, myUserId, auth?.userRole)
              : true
          }
          onClose={() => {
            setEventModalOpen(false)
            setEditingSchedule(null)
          }}
          onSubmit={handleSubmit}
          onDelete={
            editingSchedule &&
            canEditSchedule(editingSchedule, myUserId, auth?.userRole)
              ? handleDelete
              : undefined
          }
        />
      )}

      {detailSchedule && (
        <ScheduleDetailModal
          schedule={detailSchedule}
          canEdit={canEditSchedule(detailSchedule, myUserId, auth?.userRole)}
          onClose={() => setDetailSchedule(null)}
          onEdit={() => openEdit(detailSchedule)}
        />
      )}

      {dayDetailKey && (
        <ScheduleDayDetailModal
          dateKey={dayDetailKey}
          schedules={dayDetailSchedules}
          onClose={() => setDayDetailKey(null)}
          onSelectSchedule={(s) => {
            setDayDetailKey(null)
            setDetailSchedule(s)
          }}
          onCreate={() => {
            const key = dayDetailKey
            setDayDetailKey(null)
            openCreate(key)
          }}
        />
      )}
    </div>
  )
}
